import {
  MarketInputTextCustomEvent,
  MarketListCustomEvent,
  TMarketListSelectionsDidChangeEventDetail,
} from '@market/web-components';
import { ChevronDown } from '@squareup/dex-icons/dex/control';
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import { Box } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import {
  MarketDropdown,
  MarketInputText,
  MarketList,
  MarketPopover,
} from '@squareup/dex-ui-shared-market';
import { isPrintableCharacter } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, {
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ComboboxPopoverRow } from './ComboboxPopoverRow';
import { useFilteredComboboxValues } from './combobox-hooks';
import { ComboboxOption } from './combobox-types';
import styles from './combobox.module.css';

const popoverPlacementDatasetName = 'popperPlacement';

const strings = {
  noResultsFound: {
    title: 'No results',
    description: 'Clear field to see options',
  },
};

interface Props {
  value?: string | undefined;
  onChange: (value: string) => void;
  actionButton?: ReactNode;
  extrasButton?: ReactNode;
  comboboxValues?: Array<ComboboxOption> | undefined;
  placeholder?: string | undefined;
  readOnly?: boolean;
  // Accessible name attribute
  name: string;
  type?: string | undefined;
  leadingAccessory?: ReactNode;
}

// A combobox is a field that can be typed in, while also having a dropdown
const Combobox: FC<Props> = ({
  value,
  onChange,
  actionButton,
  extrasButton,
  comboboxValues,
  name,
  placeholder,
  type,
  readOnly = false,
  leadingAccessory,
}) => {
  const dropdownRef = useRef<globalThis.HTMLMarketDropdownElement>(null);
  const inputRef = useRef<globalThis.HTMLMarketInputTextElement>(null);
  const listRef = useRef<globalThis.HTMLMarketListElement>(null);
  const chevronIcon = useRef<globalThis.HTMLDivElement>(null);
  const popoverRef = useRef<globalThis.HTMLMarketPopoverElement>(null);

  // We need a way to know if the user is currently typing,
  // or the value in the field was already there when they clicked the dropdown
  // If the user is typing, we want to filter the dropdown values
  // Otherwise, we need to show all possible values
  const [typedInputValue, setTypedInputValue] = useState('');

  const filteredComboboxValues = useFilteredComboboxValues(
    comboboxValues,
    typedInputValue
  );

  // What is happening is that the `MarketDropdown` cannot properly handle it when a
  // MarketPopover with rows is injected into the expected slot after rendering.
  // It requires a complete re-render to fix itself, otherwise it has that larger-looking input.
  // This happens specifically when you are logged in, and we attempt to query for things like location ids early.
  //  After injecting them in, the bug results.
  // To fix it, we will simply re-render the `MarketDropdown` using the `key`
  // field whenever the `comboboxValues` prop changes. `key` ensures the component will re-render if it changes.
  const marketDropdownRerenderKey = useMemo(() => {
    // Lazy hash code function
    return comboboxValues?.map((val) => val.value).join('-');
  }, [comboboxValues]);

  const hasPopover = (comboboxValues?.length ?? 0) > 0;

  const [isMarketComponentLoaded, onMarketRef] = useIsMarketComponentLoaded(
    dropdownRef,
    'market-dropdown'
  );

  const focusInputElement = useCallback(() => {
    inputRef.current?.setFocus(true);
  }, []);

  // The only way I've found to write to the input while triggering
  // the market events is to write a value to the input element,
  // and then dispatch an input event
  const writeToInput = useCallback(
    async (s: string, concatenateValue: boolean = false) => {
      const inputElement = await inputRef.current?.getInputElement();
      if (inputElement) {
        const newValue = concatenateValue ? inputElement.value + s : s;
        inputElement.value = newValue;
        inputElement.dispatchEvent(new Event('input'));
      }
    },
    []
  );

  const closeDropdown = useCallback(() => {
    inputRef.current?.blur();
    dropdownRef.current?.closeDropdown();
  }, []);

  const onCloseDropdown = useCallback(() => {
    setTypedInputValue('');
  }, []);

  const onInputClick = useCallback(async () => {
    if (hasPopover) {
      const inputElement = await inputRef.current?.getInputElement();
      inputElement?.select();
    }
  }, [hasPopover]);

  const onInputValueChange = useCallback(
    ({ detail }: MarketInputTextCustomEvent<{ value: string }>) => {
      setTypedInputValue(detail.value);
      onChange(detail.value);
    },
    [onChange]
  );

  const onInputKeyDown = useCallback<
    KeyboardEventHandler<globalThis.HTMLMarketInputTextElement>
  >(
    async (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown();
      } else if (event.key === 'Enter') {
        event.preventDefault();

        // Select the first value if there is only one value in the dropdown
        if (filteredComboboxValues.length === 1) {
          closeDropdown();
          onChange(filteredComboboxValues?.[0]?.value ?? '');
        } else if (!dropdownRef.current?.expanded) {
          await dropdownRef.current?.openDropdown();
        }
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!dropdownRef.current?.expanded) {
          await dropdownRef.current?.openDropdown();
        }

        // The popover can appear above or below the input field.
        // Ensure he handle the correct direction
        if (
          event.key === 'ArrowDown' &&
          popoverRef.current?.dataset[popoverPlacementDatasetName]?.startsWith(
            'bottom'
          )
        ) {
          listRef.current?.focusRowAtIndex(0);
        } else if (
          event.key === 'ArrowUp' &&
          popoverRef.current?.dataset[popoverPlacementDatasetName]?.startsWith(
            'top'
          )
        ) {
          listRef.current?.focusRowAtIndex(filteredComboboxValues.length - 1);
        }
      }
    },
    [closeDropdown, filteredComboboxValues, onChange]
  );

  const onComboboxRowArrowUp = useCallback(() => {
    if (
      popoverRef.current?.dataset[popoverPlacementDatasetName]?.startsWith(
        'bottom'
      )
    ) {
      focusInputElement();
    }
  }, [focusInputElement]);

  const onComboboxRowArrowDown = useCallback(() => {
    if (
      popoverRef.current?.dataset[popoverPlacementDatasetName]?.startsWith(
        'top'
      )
    ) {
      focusInputElement();
    }
  }, [focusInputElement]);

  const onPopoverSelectionChange = useCallback(
    ({
      detail,
    }: MarketListCustomEvent<TMarketListSelectionsDidChangeEventDetail>) => {
      onChange(detail.newSelectionValue);
      focusInputElement();
    },
    [focusInputElement, onChange]
  );

  const onPopoverKeyDown = useCallback<KeyboardEventHandler<HTMLElement>>(
    (e) => {
      if (!e.metaKey && isPrintableCharacter(e.key)) {
        e.preventDefault();
        writeToInput(e.key, true);
        focusInputElement();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        writeToInput(inputRef.current?.value?.slice(0, -1) ?? '');
        focusInputElement();
      }
    },
    [focusInputElement, writeToInput]
  );

  const trackingExtra = useMemo(() => {
    return JSON.stringify({ name });
  }, [name]);

  const onTrailingAccessoryClick = useCallback<MouseEventHandler<HTMLElement>>(
    (e) => {
      // A small hack, but I don't see any other way. Right now,
      // if you click on anything in the trailing accessory, it will give focus
      // to the input field, and you cannot prevent this event from bubbling it seems.
      // The only workaround I can see it to trigger a blur event on the input element, and
      // to forcibly close the dropdown when interacting with it unless they click the chevron icon
      if (!chevronIcon.current?.contains(e.target as Node)) {
        closeDropdown();
      }
    },
    [closeDropdown]
  );

  const trailingAccessory = (
    <Box
      slot="trailing-accessory"
      padding={{ horizontal: '1.5x' }}
      className={styles['trailing-acc']}
      onClick={onTrailingAccessoryClick}
    >
      {extrasButton}
      {actionButton}
      {hasPopover && (
        <Box className={styles['chevron-container']} ref={chevronIcon}>
          <ChevronDown
            className={clsx(styles.chevron, commonIconStyles['icon-color-20'])}
          />
        </Box>
      )}
    </Box>
  );

  const onNoResultsEntryClicked = useCallback<
    MouseEventHandler<globalThis.HTMLMarketRowElement>
  >(() => {
    writeToInput('');
    focusInputElement();
    dropdownRef.current?.openDropdown();
  }, [focusInputElement, writeToInput]);

  const noResultsEntry = (
    <ComboboxPopoverRow
      value={strings.noResultsFound.title}
      description={strings.noResultsFound.description}
      onClick={onNoResultsEntryClicked}
    />
  );

  return (
    <Box
      className={clsx(
        styles.wrapper,
        !isMarketComponentLoaded && styles['hidden']
      )}
    >
      <MarketDropdown
        key={marketDropdownRerenderKey}
        className={clsx(styles.dropdown)}
        popoverPlacement="bottom-start"
        ref={onMarketRef}
        onMarketDropdownClosed={onCloseDropdown}
      >
        {hasPopover && (
          <MarketPopover
            slot="popover"
            className={styles.popover}
            ref={popoverRef}
            onKeyDown={onPopoverKeyDown}
          >
            {filteredComboboxValues.length > 0 ? (
              <MarketList
                onMarketListSelectionsDidChange={onPopoverSelectionChange}
                ref={listRef}
              >
                {filteredComboboxValues.map(
                  (
                    { value: comboboxValue, displayText, description },
                    index
                  ) => (
                    <ComboboxPopoverRow
                      key={comboboxValue}
                      displayText={displayText}
                      value={comboboxValue}
                      description={description}
                      highlightText={value}
                      onArrowUp={index === 0 ? onComboboxRowArrowUp : undefined}
                      onArrowDown={
                        index === filteredComboboxValues.length - 1
                          ? onComboboxRowArrowDown
                          : undefined
                      }
                    />
                  )
                )}
              </MarketList>
            ) : (
              // Gotta use a whole new list with a set value, otherwise the
              // no results value gets highlighted on click
              <MarketList value={'0'}>{noResultsEntry}</MarketList>
            )}
          </MarketPopover>
        )}
        {/* We need the input text to appear after the popover. 
            The gist of this is because of this line - https://github.com/squareup/market/blob/main/web/web-components/src/components/market-dropdown/market-dropdown.tsx#L246
            Because of our support for an `extrasButton` which may have it's own MarketDropdown, we end up having a MarketDropdown within a MarketDropdown.
            The MarketDropdown looks for the popover slot with a basic `element.querySelector`. Because the extrasButton MarketDropdown appears in the input, the querySelector
            would find that popover first. This meant the main popover would not be pushed into the proper popover management.
            The simple fix is just to re-arrange our DOM nodes to make this work.
        */}
        <MarketInputText
          className={clsx(styles.input)}
          // "off" should disable browser autofill in most cases
          autocomplete="off"
          readonly={readOnly}
          onMarketInputValueChange={onInputValueChange}
          onKeyDown={onInputKeyDown}
          onClick={onInputClick}
          size="small"
          slot="trigger"
          testId="combobox"
          trackingId={`api-explorer-combobox-input-${name}`}
          trackingExtra={trackingExtra}
          ref={inputRef}
          name={name}
          {...(type && { type })}
          {...(value && { value })}
          {...(placeholder && { placeholder })}
        >
          {leadingAccessory}
          {trailingAccessory}
        </MarketInputText>
      </MarketDropdown>
    </Box>
  );
};

export { Combobox };
